"use client";

import { useTranslation } from 'react-i18next';
import { useEffect } from 'react';
import { usePathname, useRouter } from 'next/navigation';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Languages } from 'lucide-react';
import { useUserProfile } from '@/hooks/useUserProfile';
import { useAuth } from '@/hooks/useAuth';

export function LanguageToggle() {
  const { i18n } = useTranslation();
  const { user } = useAuth();
  const { profile, updateLanguage } = useUserProfile();
  const pathname = usePathname();
  const router = useRouter();

  const languages = [
    { code: 'en', label: 'English', flag: '🇺🇸' },
    { code: 'pt', label: 'Português', flag: '🇧🇷' },
  ];

  // The public SEO surface (/plans, /tools, /blog) is bilingual *by URL*:
  // English at the root, Portuguese under a /pt prefix. There the page content
  // is server-rendered from the URL, not the runtime i18n toggle — so switching
  // language must navigate to the mirrored URL, and the chrome language must
  // follow the URL. Everywhere else the toggle just flips runtime i18n.
  const isPt = pathname === '/pt' || (pathname?.startsWith('/pt/') ?? false);
  const basePath = isPt ? (pathname!.replace(/^\/pt/, '') || '/') : (pathname ?? '/');
  const isSeoSurface = ['/plans', '/tools', '/blog'].some(
    (p) => basePath === p || basePath.startsWith(`${p}/`),
  );
  const urlLocale = isPt ? 'pt' : 'en';

  // Off the SEO surface: sync language from the user profile (existing behavior).
  useEffect(() => {
    if (isSeoSurface) return;
    if (profile?.language && profile.language !== i18n.language) {
      i18n.changeLanguage(profile.language);
    }
  }, [profile?.language, i18n, isSeoSurface]);

  // On the SEO surface: chrome language follows the URL locale, so a direct
  // visit to /pt/... (e.g. from search) renders Portuguese nav/footer too.
  useEffect(() => {
    if (isSeoSurface && i18n.language !== urlLocale) {
      i18n.changeLanguage(urlLocale);
    }
  }, [isSeoSurface, urlLocale, i18n]);

  const handleLanguageChange = async (langCode: string) => {
    i18n.changeLanguage(langCode);

    // Save to user profile if logged in
    if (user) {
      await updateLanguage(langCode);
    }

    // On the bilingual SEO surface, move to the mirrored URL so the
    // server-rendered page content switches language too.
    if (isSeoSurface) {
      const target = langCode === 'pt' ? `/pt${basePath}` : basePath;
      if (target !== pathname) router.push(target);
    }
  };

  const currentLanguage = languages.find(lang => lang.code === i18n.language) || languages[0];

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="sm" className="gap-2">
          <Languages className="h-4 w-4" />
          <span className="hidden sm:inline">{currentLanguage.flag}</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {languages.map((lang) => (
          <DropdownMenuItem
            key={lang.code}
            onClick={() => handleLanguageChange(lang.code)}
            className={i18n.language === lang.code ? 'bg-accent' : ''}
          >
            <span className="mr-2">{lang.flag}</span>
            {lang.label}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
